import { createContext, useContext, useRef } from "react";
import { type StoreApi, useStore } from "zustand";
import type { TimeRange } from "./ChartSpec";
import { type CrosshairBus, createCrosshairBus } from "./crosshair/crosshair-bus";

import { createDataViewerStore, type DataViewerState } from "./data-viewer-store";

const DataViewerContext = createContext<StoreApi<DataViewerState> | null>(null);
const CrosshairBusContext = createContext<CrosshairBus | null>(null);

export function DataViewerProvider({
    children,
    defaultTimeRange,
}: {
    children: React.ReactNode;
    defaultTimeRange?: TimeRange;
}) {
    const storeRef = useRef<StoreApi<DataViewerState> | null>(null);
    if (!storeRef.current) {
        storeRef.current = createDataViewerStore(defaultTimeRange);
    }

    const crosshairBusRef = useRef<CrosshairBus | null>(null);
    if (!crosshairBusRef.current) {
        crosshairBusRef.current = createCrosshairBus();
    }

    const store = storeRef.current;
    const crosshairBus = crosshairBusRef.current;

    return (
        <DataViewerContext.Provider value={store}>
            <CrosshairBusContext.Provider value={crosshairBus}>{children}</CrosshairBusContext.Provider>
        </DataViewerContext.Provider>
    );
}

export function useDataViewerStore<T>(selector: (s: DataViewerState) => T) {
    const store = useContext(DataViewerContext);
    if (!store) {
        throw new Error("useDataViewerStore must be used within a DataViewerProvider");
    }
    return useStore(store, selector);
}

export function useCrosshairBus(): CrosshairBus {
    const ctx = useContext(CrosshairBusContext);
    if (!ctx) {
        throw new Error("useCrosshairBus must be used within a DataViewerProvider");
    }
    return ctx;
}
